import { useEffect, useState } from 'react'
import { formatMoney, toNumber } from '@shop/shared'
import { Banner, ErrorBanner } from '@shop/ui'
import { Figure, Keypad, Sheet, TouchButton } from '../pos/touch'
import * as pos from '../pos/api'
import type { SaleDetail } from '../pos/contracts'

/**
 * الدفع — <b>نقدًا أو بطاقةً أو كليك أو مختلطًا، والمجموعُ بالضبط</b>.
 *
 * ⚠ <b>وكليك (طلبُ المالك ٢٠٢٦-١٠-٠٤) لا يدخل الدرج</b>: المالُ في حساب المحلّ،
 *   فلا يزيد النقدَ المتوقَّع عند جرد الورديّة، ويُرحَّل إلى البنك. ومرجعُ التحويل
 *   اختياريٌّ — الكاشيرُ يرى الإشعارَ على هاتفه، والرقمُ للمطابقة مع كشف البنك.
 *
 * ⚠ <b>ولا دفعٌ ناقصٌ ولا زائد</b> (ب-٥): المجموعُ يساوي المطلوبَ بالضبط.
 *   والزائدُ مالٌ لا يقابله بيع. والخادمُ يرفض، <b>والقاعدةُ ترفض بعده</b> —
 *   وهذه الشاشةُ تمنع الوصولَ إلى الرفض.
 *
 * ⚠ <b>والآجلُ سطرُ دفعٍ ثالثٌ لا «دفعٌ ناقص»</b> (المرحلة الخامسة): ما يبقى
 *   على الزبون يُكتب في خانة «على الحساب» فيكتمل المجموع — <b>ويصير ذمّةً
 *   مسمّاةً لا نقصًا مجهولًا</b>. ونصفُه نقدًا ونصفُه آجلًا يعمل بلا حالةٍ
 *   خاصّة، وهو أشيعُ ما يقع في محلّ هواتف.
 *
 * ⚠ <b>ولا آجلَ بلا زبونٍ مسجَّل</b>: الخادمُ يرفض، والشاشةُ تقول لماذا قبل
 *   أن يكتب الكاشيرُ رقمًا.
 *
 * <b>والفكّةُ تُحسب هنا للعرض وحدَها</b> — ولا تُرسل: الخادمُ يحسبها من
 * <c>tendered − amount</c> ولا يُخزَّن لها صفٌّ سالب.
 */
export default function Payment({
  sale,
  onDone,
  onClose,
}: {
  sale: SaleDetail
  onDone: (completed: SaleDetail) => void
  onClose: () => void
}) {
  const total = sale.header.total

  // ⚠ **والنقدُ مملوءٌ بالمطلوب سلفًا** — قرارُ المالك «قلّل الخطوات»:
  //   أكثرُ البيعات نقدًا بالضبط، فتصير الفاتورةُ <kbd>F10</kbd> ثمّ
  //   <kbd>Enter</kbd> بلا رقمٍ يُكتب. ومن سلّم أكثرَ يكتب المُسلَّمَ فتُحسب
  //   الفكّة، ومن دفع بالبطاقة يضغط زرَّها فيُفرَّغ النقد.
  const [cash, setCash] = useState(String(total))
  const [card, setCard] = useState('')
  const [cliq, setCliq] = useState('')
  const [cliqRef, setCliqRef] = useState('')
  const [credit, setCredit] = useState('')
  const [reference, setReference] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)

  const cashValue = toNumber(cash) ?? 0
  const cardValue = toNumber(card) ?? 0
  const cliqValue = toNumber(cliq) ?? 0
  const creditValue = toNumber(credit) ?? 0

  // والآجلُ لا يقبل «مُسلَّمًا»: لا مالَ خرج من يدٍ، فلا فكّةَ تُحسب منه.
  const onAccount = sale.header.customerId === null ? 0 : creditValue

  // ما يُطبَّق نقدًا: المُسلَّمُ قد يزيد، والزائدُ فكّةٌ لا دفعة.
  const cashApplied = Math.min(cashValue, Math.max(total - cardValue - cliqValue - onAccount, 0))
  const applied = cashApplied + cardValue + cliqValue + onAccount
  const change = Math.max(cashValue - cashApplied, 0)
  const remaining = total - applied

  const exact = Math.abs(remaining) < 0.0005
  const needsReference = cardValue > 0 && reference.trim() === ''

  const pay = async () => {
    setBusy(true)
    setError(null)

    try {
      const payments = []

      if (cashApplied > 0) {
        payments.push({
          methodCode: 'CASH',
          amount: round(cashApplied),
          // والمُسلَّمُ يُرسل حين يزيد — ليُطبع على الفاتورة مع الفكّة.
          tendered: cashValue > cashApplied ? round(cashValue) : undefined,
        })
      }

      if (cardValue > 0) {
        payments.push({ methodCode: 'CARD', amount: round(cardValue), referenceNo: reference.trim() })
      }

      if (cliqValue > 0) {
        payments.push({
          methodCode: 'CLIQ',
          amount: round(cliqValue),
          referenceNo: cliqRef.trim() === '' ? undefined : cliqRef.trim(),
        })
      }

      if (onAccount > 0) {
        payments.push({ methodCode: 'CREDIT', amount: round(onAccount) })
      }

      onDone(await pos.completeSale(sale.header.id, payments))
    } catch (failure) {
      setError(failure)
    } finally {
      setBusy(false)
    }
  }

  const canPay = !busy && exact && !needsReference

  /**
   * <kbd>Enter</kbd> يُتمّ، و<kbd>Esc</kbd> يُغلق — <b>ولا فأرةَ في المسار
   * الشائع</b>.
   *
   * ⚠ <b>ولا يُتمّ إلا إن كان المجموعُ مضبوطًا</b>: <kbd>Enter</kbd> على
   *   مجموعٍ ناقصٍ كان سيُطلق طلبًا يرفضه الخادم، فيبدو الزرُّ عطبان.
   */
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Enter' && canPay) {
        event.preventDefault()
        void pay()
        return
      }

      if (event.key === 'Escape' && !busy) {
        event.preventDefault()
        onClose()
      }
    }

    window.addEventListener('keydown', onKey)

    return () => window.removeEventListener('keydown', onKey)
  })

  return (
    <Sheet title={`الدفع — ${formatMoney(total)}`} onClose={onClose}>
      <div className="mx-auto grid max-w-md gap-4">
        <div className="grid grid-cols-2 gap-2">
          <Figure label="المطلوب" value={formatMoney(total)} />
          <Figure
            label={remaining > 0 ? 'الباقي' : 'الفكّة'}
            value={formatMoney(remaining > 0 ? remaining : change)}
            tone={remaining > 0 ? 'warn' : 'ok'}
          />
        </div>

        <div className="grid gap-2">
          <Slot
            label="نقدًا"
            value={cash}
            onChange={setCash}
            hint="اكتب ما سلّمه الزبونُ — والفكّةُ تُحسب."
          />

          <Slot label="بطاقة" value={card} onChange={setCard} />

          <Slot label="كليك" value={cliq} onChange={setCliq} />

          {cliqValue > 0 && (
            <label className="block">
              <span className="mb-1 block text-xs text-ink-soft">رقمُ حركة كليك (اختياريّ)</span>
              <input
                className="min-h-12 w-full rounded-lg border border-line bg-surface px-3"
                dir="ltr"
                value={cliqRef}
                disabled={busy}
                placeholder="للمطابقة مع إشعار البنك"
                onChange={(event) => setCliqRef(event.target.value)}
              />
            </label>
          )}

          {/*
            ⚠ **وخانةُ الآجل لا تظهر بلا زبونٍ مسجَّل** — والرسالةُ تقول
              الطريقَ إلى الحلّ لا «غيرُ مسموح» وحدَها.
          */}
          {sale.header.customerId !== null ? (
            <Slot
              label={`على حساب ${sale.header.customerName ?? 'الزبون'}`}
              value={credit}
              onChange={setCredit}
              hint="يبقى دَينًا على الزبون ويظهر في كشف حسابه."
            />
          ) : (
            <Banner tone="info">
              للبيع على الحساب: اختر زبونًا مسجَّلًا أوّلًا — والذمّةُ تحتاج اسمًا يُطالَب.
            </Banner>
          )}

          {cardValue > 0 && (
            <label className="block">
              <span className="mb-1 block text-xs text-ink-soft">رقمُ مرجع البطاقة</span>
              <input
                className="min-h-12 w-full rounded-lg border border-line bg-surface px-3"
                value={reference}
                disabled={busy}
                placeholder="رقمُ الإيصال"
                onChange={(event) => setReference(event.target.value)}
              />
              {/* ⚠ ولا رقمَ بطاقةٍ هنا: القاعدةُ ترفض سلسلةً من ١٣–١٩ رقمًا. */}
              <span className="mt-1 block text-xs text-ink-soft">
                رقمُ الإيصال لا رقمُ البطاقة — والقاعدةُ ترفض رقمَ بطاقة.
              </span>
            </label>
          )}
        </div>

        <Keypad
          value={cashValue === 0 && cliqValue > 0 ? cliq : cardValue > 0 && cashValue === 0 ? card : cash}
          onChange={cashValue === 0 && cliqValue > 0 ? setCliq : cardValue > 0 && cashValue === 0 ? setCard : setCash}
        />

        <div className="grid grid-cols-3 gap-2">
          <TouchButton onClick={() => { setCard(''); setCliq(''); setCash(String(total)) }}>نقدًا بالضبط</TouchButton>
          <TouchButton onClick={() => { setCash(''); setCliq(''); setCard(String(total)) }}>
            بطاقةً بالكامل
          </TouchButton>
          <TouchButton onClick={() => { setCash(''); setCard(''); setCliq(String(total)) }}>
            كليك بالكامل
          </TouchButton>
        </div>

        <p className="text-center text-[11px] text-ink-soft">
          <kbd>Enter</kbd> يُتمّ الفاتورة · <kbd>Esc</kbd> يُغلق
        </p>

        {sale.header.customerId !== null && (
          <TouchButton onClick={() => { setCash(''); setCard(''); setCliq(''); setCredit(String(total)) }}>
            كلُّه على الحساب
          </TouchButton>
        )}

        <ErrorBanner error={error} />

        {!exact && applied > 0 && (
          <Banner tone="warn">
            المجموعُ يجب أن يساوي المطلوبَ بالضبط — لا ناقصَ ولا زائد.
          </Banner>
        )}

        <TouchButton
          tone="primary"
          wide
          disabled={busy || !exact || needsReference}
          onClick={() => void pay()}
        >
          {busy ? 'يُتمّ…' : 'أتمِم البيع'}
        </TouchButton>
      </div>
    </Sheet>
  )
}

function Slot({
  label,
  value,
  onChange,
  hint,
}: {
  label: string
  value: string
  onChange: (value: string) => void
  hint?: string
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs text-ink-soft">{label}</span>
      <input
        className="tabular min-h-14 w-full rounded-xl border border-line bg-surface px-3 text-lg"
        dir="ltr"
        inputMode="decimal"
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
      {hint && <span className="mt-1 block text-xs text-ink-soft">{hint}</span>}
    </label>
  )
}

/** الدينارُ ثلاثُ خانات — والتقريبُ مرّةً واحدةً عند الحدّ. */
function round(value: number) {
  return Math.round(value * 1000) / 1000
}
